import React, { useState } from 'react';
import checkCircleOutiline from '../../assets/check_circle_outline.svg';
import clock from '../../assets/clock.svg';
import helpIcon from '../../assets/helpIcon.svg';
import pin from '../../assets/pin_drop.svg';
import place from '../../assets/place.svg';
import { ReactComponent as ChevronDown } from '../../assets/chevron-down.svg';
import { ReactComponent as ChevronUp } from '../../assets/chevron-up.svg';
import { useBrandContext } from '../../context/BrandContext';
import Button from '../button/Button';
import ToastHelp from '../toastHelp/ToastHelp';
import ToastMapsOptions from '../toastMapsOptions/ToastMapsOptions';
import './ChargerStationDetails.css';
import { formatKm } from '../../utils/format';
import { useToastStore } from '../../hooks/useToastStore';
import roaming from '../../assets/world_roaming_icon.svg';

type TypeHours = { isOpen: boolean; mode: number; from: string; to: string };

interface ChargerStationDetailsProps {
  details: {
    name: string;
    address: string;
    parkingInfo: string;
    operationHours: string;
    addressDistance: number;
    cmcType: string;
    csPrivate: string;
    lat: number;
    long: number;
    isOcpi?: boolean;
    hoursData?: TypeHours[];
    isOpen?: boolean;
  };
}

const ChargerStationDetails: React.FC<ChargerStationDetailsProps> = ({
  details
}) => {
  const brandContext = useBrandContext();
  const [isHoursExpanded, setIsHoursExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const {
    showHelpToast,
    showDirectionsToast,
    setShowHelpToast,
    setShowDirectionsToast
  } = useToastStore();

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${details?.lat},${details?.long}`;
  const appleURl = `http://maps.apple.com/?q=${details?.lat},${details?.long}`;
  const wazeUrl = `https://waze.com/ul?ll=${details?.lat},${details?.long}&navigate=yes`;

  const dayLabels = [
    'Domingo',
    'Segunda-Feira',
    'Terça-Feira',
    'Quarta-Feira',
    'Quinta-Feira',
    'Sexta-Feira',
    'Sábado'
  ];

  // Obter o dia atual da semana (0 = Domingo, 1 = Segunda, etc.)
  const getCurrentDayIndex = () => {
    return new Date().getDay();
  };

  // Função para lidar com o clique de forma segura
  const handleToggleClick = () => {
    if (isAnimating) return; // Bloquear cliques durante animação

    setIsAnimating(true);
    setIsHoursExpanded(!isHoursExpanded);

    // Liberar após a duração da animação (300ms + margem de segurança)
    setTimeout(() => {
      setIsAnimating(false);
    }, 400);
  };

  const renderOperationHours = () => {
    if (!details.hoursData) {
      return <span>{details.operationHours}</span>;
    }

    return (
      <div className="operation-hours-container">
        <div
          className="operation-hours-header"
          onClick={handleToggleClick}
          style={{
            cursor: isAnimating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>
            {details.csPrivate ? 'Privado' : 'Público'} /{' '}
            {details.isOpen ? 'Aberto' : 'Fechado'} *
          </span>
          {isHoursExpanded ? <ChevronUp /> : <ChevronDown />}
        </div>

        {/* Dia atual sempre visível - apenas quando não expandido */}
        {!isHoursExpanded &&
          details.hoursData &&
          details.hoursData[getCurrentDayIndex()] && (
            <div className="charger-station-detail-lines">
              <div className="charger-station-day-label">
                {dayLabels[getCurrentDayIndex()]}
              </div>
              <div>
                {details.hoursData[getCurrentDayIndex()].isOpen
                  ? details.hoursData[getCurrentDayIndex()].mode === 1
                    ? '24h'
                    : `${details.hoursData[getCurrentDayIndex()].from} às ${
                        details.hoursData[getCurrentDayIndex()].to
                      }`
                  : 'Fechado'}
              </div>
            </div>
          )}

        {/* Todos os horários quando expandido, começando pelo dia atual */}
        {isHoursExpanded && (
          <div className="operation-hours-content">
            {/* Criar array ordenado começando pelo dia atual */}
            {(() => {
              const currentDayIndex = getCurrentDayIndex();
              const orderedDays = [];

              // Adicionar dias começando pelo atual até o final da semana
              for (let i = currentDayIndex; i < 7; i++) {
                orderedDays.push(i);
              }

              // Adicionar dias do início da semana até o dia anterior ao atual
              for (let i = 0; i < currentDayIndex; i++) {
                orderedDays.push(i);
              }

              return orderedDays.map((dayIndex) => (
                <div key={dayIndex} className="charger-station-detail-lines">
                  <div className="charger-station-day-label">
                    {dayLabels[dayIndex]}
                  </div>
                  <div>
                    {details.hoursData?.[dayIndex]?.isOpen
                      ? details.hoursData[dayIndex].mode === 1
                        ? '24h'
                        : `${details.hoursData[dayIndex].from} às ${details.hoursData[dayIndex].to}`
                      : 'Fechado'}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {details?.name && (
        <div className="charger-station-details">
          <h1 className="charger-station-name">
            {details.name}{' '}
            {details?.isOcpi && (
              <img
                className="world-roaming-icon"
                loading="lazy"
                alt="roaming-icon"
                src={roaming}
              />
            )}
          </h1>
          <div className="charger-station-detail-lines">
            <img
              src={place}
              className={'charger-icon charger-icon-blue'}
              alt="Local"
            />

            <span className="charger-station-detail-lines_text charger-station-address">
              <b>{formatKm(details.addressDistance)} km </b> - {details.address}
            </span>
          </div>
          <div className="charger-station-detail-lines">
            <img
              src={checkCircleOutiline}
              className={'charger-icon'}
              alt="Aberto"
            />
            <span className="charger-station-detail-lines_text charger-station-parking-info">
              {details.parkingInfo}
            </span>
          </div>
          <div className="charger-station-detail-lines">
            <img
              src={clock}
              className={'charger-icon charger-icon-blue'}
              alt="Horario"
            />
            <div className="charger-station-detail-lines_text charger-station-operation-hours">
              {renderOperationHours()}
            </div>
          </div>

          <div className="actions-container">
            <Button
              className="btn-outline-md"
              type="button"
              children="Como Chegar"
              spinnerSecondary={true}
              onClick={() => setShowDirectionsToast(true)}
              image={<img src={pin} alt="Como Chegar" />}
            />

            <Button
              className="btn-outline-md"
              type="button"
              children="Ajuda"
              spinnerSecondary={true}
              onClick={() => setShowHelpToast(true)}
              image={<img src={helpIcon} alt="Como Chegar" />}
            />
          </div>

          <div className="charger-station-details-text-content">
            Escolha um carregador
          </div>
        </div>
      )}
      <ToastMapsOptions
        googleMapsUrl={mapUrl}
        appleMapsUrl={appleURl}
        wazeUrl={wazeUrl}
        isModalOpen={showDirectionsToast}
        allowButtonClosed={true}
        setShowToastMapsOptions={setShowDirectionsToast}
      />

      <ToastHelp
        isModalOpen={showHelpToast}
        allowButtonClosed={true}
        setShowHelpToast={setShowHelpToast}
      />
    </>
  );
};

export default ChargerStationDetails;
